import {
	type QueryClient,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";
import { useOptionalAuthUser } from "@/features/auth/auth-provider";
import { TenantSwitcherContext } from "@/features/tenant-switcher/tenant-switcher-context";
import { ApiError } from "@/lib/auth-api";
import { getPlanStore, type PlanStore, type RunnerDeps, usePlanSnapshot } from "./plan-store";
import { CONFLICT_MESSAGES } from "./printer-labels";
import { startPlanAction } from "./printer-plan";
import {
	type DiscoverResponse,
	discoverNetworkPrinters,
	getNetworkPrinters,
	getPharmacyPrinters,
	getPrinterOperation,
	listPrinterOperations,
	listPrinterPharmacies,
	type NetworkInfo,
	type NetworkPrintersResponse,
	type PharmaciesResponse,
	type PharmacyPrintersResponse,
	type PrinterDevice,
	type PrinterOperation,
	type PrinterPlace,
	type RefreshResponse,
	refreshPharmacyPrinters,
} from "./printers-api";
import { observeOperation } from "./step-timeline";

/**
 * Klucze modułu: `["printers", zasób, tenant, …]`. Tenant z przełącznika
 * organizacji (super_admin), bo bez niego po przepięciu widać dane poprzedniej.
 * Miejsce: apteka po idfirm, grupa jako `g:<group>` — nie zderzą się z liczbą.
 */
const placeKey = (place: PrinterPlace): number | string =>
	place.kind === "pharmacy" ? place.idfirm : `g:${place.group}`;

export const PRINTER_PHARMACIES_QUERY_KEY = (tenantId: number | null) =>
	["printers", "pharmacies", tenantId] as const;
export const PHARMACY_PRINTERS_QUERY_KEY = (tenantId: number | null, place: PrinterPlace) =>
	["printers", "pharmacy", tenantId, placeKey(place)] as const;
export const PRINTER_OPERATIONS_QUERY_KEY = (tenantId: number | null, place: PrinterPlace | null) =>
	["printers", "operations", tenantId, place ? placeKey(place) : "all"] as const;
export const PRINTER_OPERATION_QUERY_KEY = (tenantId: number | null, operationId: number) =>
	["printers", "operation", tenantId, operationId] as const;
/** Drukarki sieciowe do dodania na komputerze — klucz po urządzeniu, nie po miejscu. */
export const NETWORK_PRINTERS_QUERY_KEY = (tenantId: number | null, deviceUuid: string) =>
	["printers", "network", tenantId, deviceUuid] as const;

/** Panel odpytuje stan operacji co 2 s, dopóki jest `running` (kontrakt). */
export const OPERATION_POLL_MS = 2_000;
/** Lista drukarek sieciowych co 1,5 s, dopóki wykrywanie trwa (kontrakt: agent słucha ok. 3 s). */
export const NETWORK_PRINTERS_POLL_MS = 1_500;
/** Bezpiecznik odpytywania jednego wykrywania; potem „Wykryj ponownie”. */
export const DISCOVERY_MAX_POLL_MS = 20_000;
/** Inwentarz zmienia się rzadko; agent wysyła go po zmianie i po każdej komendzie. */
const PHARMACY_STALE_MS = 30_000;
/** Sieć komputera do mapy (pasek sieci, „inna sieć”) — zmienia się rzadko. */
const NETWORK_STALE_MS = 5 * 60_000;
/** Agent odpowiada na `printer_inventory_request` asynchronicznie — drugi odczyt po chwili. */
const REFRESH_SETTLE_MS = 8_000;

export function useTenantKey(): number | null {
	// Bez rzucania poza `TenantSwitcherProvider` (testy komponentów).
	return useContext(TenantSwitcherContext)?.selectedTenantId ?? null;
}

/** Po operacji zmienia się inwentarz apteki, liczniki na liście aptek, historia i listy sieciowe. */
export function invalidatePrinterData(queryClient: QueryClient): Promise<void> {
	return Promise.all([
		queryClient.invalidateQueries({ queryKey: ["printers", "pharmacy"] }),
		queryClient.invalidateQueries({ queryKey: ["printers", "pharmacies"] }),
		queryClient.invalidateQueries({ queryKey: ["printers", "operations"] }),
		queryClient.invalidateQueries({ queryKey: ["printers", "network"] }),
	]).then(() => undefined);
}

/** Komunikat błędu startu operacji: 409 z kodem → po polsku, reszta → treść z backendu. */
export function printerErrorMessage(error: unknown): string {
	if (error instanceof ApiError) {
		if (error.code && CONFLICT_MESSAGES[error.code]) return CONFLICT_MESSAGES[error.code];
		if (error.status === 403) return "Brak dostępu do tej apteki, grupy lub urządzenia.";
		return error.message;
	}
	return error instanceof Error ? error.message : String(error);
}

export function usePrinterPharmaciesQuery() {
	const tenantId = useTenantKey();
	return useQuery<PharmaciesResponse>({
		queryKey: PRINTER_PHARMACIES_QUERY_KEY(tenantId),
		queryFn: listPrinterPharmacies,
		staleTime: 60_000,
	});
}

/** `enabled: false` — bez pobierania, tylko to, co już jest w pamięci podręcznej (zwinięta sekcja helpdesku). */
export function usePharmacyPrintersQuery(place: PrinterPlace, enabled = true) {
	const tenantId = useTenantKey();
	return useQuery<PharmacyPrintersResponse>({
		queryKey: PHARMACY_PRINTERS_QUERY_KEY(tenantId, place),
		queryFn: () => getPharmacyPrinters(place),
		enabled,
		staleTime: PHARMACY_STALE_MS,
		refetchOnWindowFocus: true,
		// Trwająca operacja (np. rozpoczęta w innej karcie) — odśwież, aż się skończy.
		refetchInterval: (query) => (query.state.data?.runningOperation ? 5_000 : false),
	});
}

/** `place = null` — historia całego panelu. */
export function usePrinterOperationsQuery(place: PrinterPlace | null, enabled: boolean) {
	const tenantId = useTenantKey();
	return useQuery({
		queryKey: PRINTER_OPERATIONS_QUERY_KEY(tenantId, place),
		queryFn: () => listPrinterOperations(place),
		enabled,
		staleTime: PHARMACY_STALE_MS,
	});
}

/**
 * Stan jednej operacji; odpytywanie co 2 s tylko dla `running`. Gdy operacja
 * dobiegnie końca, unieważnia dane apteki (inwentarz, liczniki, historia).
 * `initial` (wiersz historii) z zakończoną operacją nie wymaga żadnego fetchu.
 * `poll: false` — tylko odczyt pamięci podręcznej, gdy odpytuje już wykonawca planu.
 */
export function usePrinterOperation(
	operationId: number | null,
	initial?: PrinterOperation | null,
	poll = true,
): { operation: PrinterOperation | null; isError: boolean; error: unknown } {
	const tenantId = useTenantKey();
	const queryClient = useQueryClient();
	const needsFetch = operationId != null && (!initial || initial.status === "running");
	const query = useQuery<PrinterOperation>({
		queryKey: PRINTER_OPERATION_QUERY_KEY(tenantId, operationId ?? 0),
		queryFn: () => getPrinterOperation(operationId as number),
		enabled: needsFetch,
		staleTime: poll ? 0 : Number.POSITIVE_INFINITY,
		refetchInterval: (q) =>
			poll && q.state.status !== "error" && (!q.state.data || q.state.data.status === "running")
				? OPERATION_POLL_MS
				: false,
	});
	const operation = (needsFetch ? query.data : undefined) ?? initial ?? null;
	// Unieważniamy dane apteki tylko przy przejściu „trwa → koniec” widzianym tutaj;
	// podgląd już zakończonej operacji (pozycja planu, historia) niczego nie odświeża.
	const sawRunning = useRef<number | null>(null);
	// Czasy kroków widziane przez panel (API ich nie podaje) — dla wodospadu.
	useEffect(() => {
		if (!needsFetch || !query.data) return;
		if (query.data.status === "running") sawRunning.current = query.data.operationId;
		observeOperation(query.data);
	}, [needsFetch, query.data]);
	const finished = needsFetch && query.data != null && query.data.status !== "running";
	const invalidatedFor = useRef<number | null>(null);
	useEffect(() => {
		if (!finished || operationId == null || sawRunning.current !== operationId) return;
		if (invalidatedFor.current === operationId) return;
		invalidatedFor.current = operationId;
		void invalidatePrinterData(queryClient);
		// Zmiany kolejek widać dopiero w świeżym inwentarzu, który agent wysyła PO acku.
		setTimeout(() => void invalidatePrinterData(queryClient), REFRESH_SETTLE_MS);
	}, [finished, operationId, queryClient]);
	return { operation, isError: query.isError, error: query.error };
}

/**
 * Drukarki sieciowe do dodania na komputerze. Tylko przy otwartym oknie
 * (`enabled`); `staleTime: 0`, bo lista zależy od trwającego wykrywania.
 * Odpytuje co 1,5 s, gdy `polling` (okno pilnuje limitu 20 s) i wykrywanie
 * jest `running`.
 */
export function useNetworkPrintersQuery(deviceUuid: string, enabled: boolean, polling: boolean) {
	const tenantId = useTenantKey();
	return useQuery<NetworkPrintersResponse>({
		queryKey: NETWORK_PRINTERS_QUERY_KEY(tenantId, deviceUuid),
		queryFn: () => getNetworkPrinters(deviceUuid),
		enabled,
		staleTime: 0,
		refetchOnWindowFocus: false,
		refetchInterval: (query) =>
			polling && query.state.data?.discovery.status === "running"
				? NETWORK_PRINTERS_POLL_MS
				: false,
	});
}

type DeviceNetworks = {
	networks: Map<string, NetworkInfo | null>;
	responses: NetworkPrintersResponse[];
};

/** Stała funkcja `combine` — React Query zwraca ten sam wynik, póki odpowiedzi się nie zmienią. */
function combineNetworks(results: { data: NetworkPrintersResponse | undefined }[]): DeviceNetworks {
	const networks = new Map<string, NetworkInfo | null>();
	const responses: NetworkPrintersResponse[] = [];
	for (const { data } of results) {
		if (!data) continue;
		networks.set(data.deviceUuid, data.network);
		responses.push(data);
	}
	return { networks, responses };
}

/**
 * Sieci komputerów do mapy: ten sam odczyt co okno „Dodaj drukarkę sieciową”
 * (`network-printers`), tylko dla komputerów online, które zgłaszają sieć
 * (`addSupported`). Widok apteki sieci nie zwraca — backend bez zmian.
 */
export function useDeviceNetworks(devices: readonly PrinterDevice[]): DeviceNetworks {
	const tenantId = useTenantKey();
	const eligible = devices.filter((d) => d.online && d.addSupported);
	return useQueries({
		queries: eligible.map((device) => ({
			queryKey: NETWORK_PRINTERS_QUERY_KEY(tenantId, device.deviceUuid),
			queryFn: () => getNetworkPrinters(device.deviceUuid),
			staleTime: NETWORK_STALE_MS,
			refetchOnWindowFocus: false,
			retry: false,
		})),
		combine: combineNetworks,
	});
}

/**
 * Start wykrywania (`checkHost` — dodatkowo jeden adres). Stan z odpowiedzi
 * 202 trafia od razu do listy (wykrywanie `running` włącza odpytywanie), a
 * lista jest pobierana od nowa — trwający odczyt sprzed startu jest anulowany,
 * więc stary stan wykrywania nie nadpisze nowego.
 */
export function useDiscoverMutation(deviceUuid: string) {
	const tenantId = useTenantKey();
	const queryClient = useQueryClient();
	const queryKey = NETWORK_PRINTERS_QUERY_KEY(tenantId, deviceUuid);
	return useMutation<DiscoverResponse, Error, string | undefined>({
		mutationFn: (checkHost) => discoverNetworkPrinters(deviceUuid, checkHost),
		onSuccess: ({ discovery }) => {
			queryClient.setQueryData<NetworkPrintersResponse>(queryKey, (prev) =>
				prev ? { ...prev, discovery } : prev,
			);
			void queryClient.invalidateQueries({ queryKey });
		},
	});
}

export function useRefreshPrintersMutation() {
	const queryClient = useQueryClient();
	return useMutation<RefreshResponse, Error, PrinterPlace>({
		mutationFn: refreshPharmacyPrinters,
		onSuccess: (result, place) => {
			toast.success(
				result.sent > 0
					? `Poproszono urządzenia o aktualną listę drukarek (${result.sent}). Widok odświeży się za kilka sekund.`
					: `Żadne urządzenie ${place.kind === "group" ? "tej grupy" : "tej apteki"} nie może teraz przesłać listy drukarek (offline albo starszy agent).`,
			);
			void invalidatePrinterData(queryClient);
			if (result.sent > 0) {
				setTimeout(() => void invalidatePrinterData(queryClient), REFRESH_SETTLE_MS);
			}
		},
		onError: (error) => toast.error(`Nie udało się odświeżyć: ${printerErrorMessage(error)}`),
	});
}

/** Klucz planu: tenant, użytkownik i miejsce — szkic jednej osoby dla jednej apteki/grupy. */
export function planStorageKey(
	tenantId: number | null,
	userId: number | null,
	place: PrinterPlace,
): string {
	return `${tenantId ?? "-"}:${userId ?? "-"}:${placeKey(place)}`;
}

/**
 * Plan zmian miejsca: magazyn (localStorage per użytkownik i miejsce),
 * wykonanie po kolei istniejącymi endpointami i obserwacja pozycji, która
 * trwała w chwili przeładowania strony.
 */
export function usePrinterPlan(place: PrinterPlace, sharing: boolean) {
	const switcherTenant = useTenantKey();
	const user = useOptionalAuthUser();
	const tenantId = switcherTenant ?? user?.tenantId ?? null;
	const queryClient = useQueryClient();
	const store: PlanStore = getPlanStore(planStorageKey(tenantId, user?.id ?? null, place));
	const snapshot = usePlanSnapshot(store);
	const viewKey = PHARMACY_PRINTERS_QUERY_KEY(switcherTenant, place);

	// biome-ignore lint/correctness/useExhaustiveDependencies: miejsce porównujemy po kluczu, nie po tożsamości obiektu
	const deps = useMemo<RunnerDeps>(
		() => ({
			start: startPlanAction,
			// Przez pamięć podręczną — wodospad planu czyta ten sam wpis bez własnego odpytywania.
			getOperation: (operationId: number) =>
				queryClient.fetchQuery({
					queryKey: PRINTER_OPERATION_QUERY_KEY(switcherTenant, operationId),
					queryFn: () => getPrinterOperation(operationId),
					staleTime: 0,
				}),
			getContext: async () => {
				const view = await queryClient.fetchQuery({
					queryKey: viewKey,
					queryFn: () => getPharmacyPrinters(place),
					staleTime: 0,
				});
				const networks = new Map<string, NetworkInfo | null>();
				for (const device of view.devices) {
					const data = queryClient.getQueryData<NetworkPrintersResponse>(
						NETWORK_PRINTERS_QUERY_KEY(switcherTenant, device.deviceUuid),
					);
					if (data) networks.set(device.deviceUuid, data.network);
				}
				return { view, sharing, networks };
			},
			onOperationFinished: () => {
				void invalidatePrinterData(queryClient);
				// Inwentarz po zmianie agent wysyła PO acku — także po ostatniej pozycji planu.
				setTimeout(() => void invalidatePrinterData(queryClient), REFRESH_SETTLE_MS);
			},
		}),
		[queryClient, placeKey(place), sharing, switcherTenant],
	);

	// Pozycja trwająca w chwili przeładowania — obserwuj do końca (plan nie rusza dalej sam).
	const hasOrphan = snapshot.items.some((i) => i.status === "running") && !snapshot.run.active;
	useEffect(() => {
		if (hasOrphan) void store.watchOrphans(deps);
	}, [hasOrphan, store, deps]);

	/** `approved` — pozycje, które operator widział i potwierdził; wykonawca nie wyjdzie poza nie. */
	const run = useCallback(
		(approved: readonly string[]) => store.run(deps, new Set(approved)),
		[store, deps],
	);
	return { store, snapshot, run };
}
